import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  Users,
  Package,
  Sparkles
} from 'lucide-react';
import { products } from '../mockData';
import ScrollReveal from '../components/ScrollReveal';

// ==========================================
// Custom Animated Counter Component
// ==========================================
const AnimatedCounter = ({ end, suffix }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (counterRef.current) observer.observe(counterRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTimestamp = null;
    const duration = 2000;
    
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      setCount(Math.floor(easeProgress * end));
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [isVisible, end]);

  return <span ref={counterRef}>{count}{suffix}</span>;
};

// ==========================================
// Static Data
// ==========================================
const features = [
  {
    icon: Award,
    title: 'Premium Quality',
    description: 'Handcrafted furniture with attention to every detail'
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: 'Experienced professionals dedicated to your workspace'
  },
  {
    icon: Package,
    title: 'Custom Solutions',
    description: 'Tailored furniture solutions for your unique needs'
  }
];

const stats = [
  { end: 500, suffix: '+', label: 'Projects Completed' },
  { end: 1000, suffix: '+', label: 'Happy Clients' },
  { end: 50, suffix: '+', label: 'Product Designs' },
  { end: 15, suffix: '+', label: 'Years Experience' }
];

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featuredProducts = useMemo(() => {
    const featured = products.filter((product) => product.featured);
    return featured.length > 0 ? featured.slice(0, 3) : products.slice(0, 3);
  }, []);

  return (
    <div className="min-h-screen overflow-hidden">
      {/* ================= HERO SECTION ================= */}
      {/* Added a dark fallback background color bg-[#111] */}
      <section className="relative h-screen flex items-center overflow-hidden bg-[#111]">
        
        {/* Using an <img> tag instead of background-image for better reliability */}
        <img
          src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2400&q=90"
          alt="Premium Office Workspace"
          className="absolute inset-0 z-0 w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.4}px)` }}
        />
        
        {/* Dark gradient overlay to make text and white navbar pop */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30 pointer-events-none"></div>

        {/* Decorative blobs adjusted for dark mode */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#C1A57B]/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#C1A57B]/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div
            className={`max-w-3xl transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-3 mb-8 shadow-sm">
              <Sparkles size={18} className="text-[#C1A57B]" />
              <span className="text-white font-medium">
                Premium Office Furniture
              </span>
            </div>

            {/* Changed text to white for the dark overlay */}
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Redefining <br />
              <span className="text-[#C1A57B]">Workspaces</span>
            </h1>

            {/* Lightened subtext for readability */}
            <p className="text-xl text-gray-200 mb-10 max-w-2xl font-medium">
              Premium office furniture designed for modern professionals.
              Elevate your workspace with AARTEMA's{' '}
              <span className="text-[#C1A57B] font-bold">
                award-winning designs
              </span>.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                className="inline-flex items-center px-8 py-4 bg-[#C1A57B] hover:bg-[#B09568] text-white font-semibold rounded-xl transition-all shadow-lg"
              >
                Explore Products
                <ArrowRight className="ml-2" size={18} />
              </Link>

              {/* Updated secondary button to match dark theme */}
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#222] font-semibold rounded-xl transition-all"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-left">
          <ScrollReveal>
            <div className="mb-16">
              <h2 className="text-5xl font-bold text-[#222] mb-4 text-center">
                Why Choose AARTEMA
              </h2>
              <div className="w-24 h-1 bg-[#C1A57B] mx-auto mb-4" />
              <p className="text-gray-600 text-lg text-center">
                Experience excellence in every detail
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="p-8 bg-[#F7F7F7] rounded-2xl hover:shadow-xl transition duration-300">
                  <feature.icon className="text-[#C1A57B] mb-6" size={36} />
                  <h3 className="text-2xl font-bold text-[#222] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURED PRODUCTS ================= */}
      <section className="py-24 bg-[#F3F3F3]">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="mb-16">
              <h2 className="text-5xl font-bold text-[#222] mb-4 text-center">
                Featured Products
              </h2>
              <div className="w-24 h-1 bg-[#C1A57B] mx-auto mb-4" />
              <p className="text-gray-600 text-lg text-center">
                Handpicked premium furniture
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            {featuredProducts.map((product, i) => (
              <ScrollReveal key={product.id || i} delay={i * 100}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition duration-300 flex flex-col h-full">
                  <img
                    src={product.images?.[0] || product.image || '/fallback-image.jpg'}
                    alt={product.name}
                    className="h-72 w-full object-cover"
                    loading="lazy"
                  />
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-[#222] mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 flex-grow">
                      {product.description || 'Premium office seating designed for ergonomic comfort.'}
                    </p>
                    <Link
                      to={`/product/${product.id}`}
                      className="text-[#C1A57B] font-semibold hover:text-[#B09568] transition-colors mt-auto inline-flex items-center"
                    >
                      View Details <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= STATS WITH COUNT UP ================= */}
      <section className="py-24 bg-[#222]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <ScrollReveal key={i}>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#C1A57B] mb-2">
                  <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                </div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-24 bg-[#C1A57B]">
        <div className="max-w-4xl mx-auto text-center px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to Transform Your Workspace?
            </h2>
            <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
              Get in touch for a free consultation and personalized quote
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 bg-white text-[#222] font-bold rounded-xl hover:bg-[#EDEDED] transition-all shadow-xl hover:-translate-y-1 duration-300"
            >
              Contact Us
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
